import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CustomLink } from '../../components/CustomLink';
import { Button } from '../../components/Button';
import { Mail, Lock, AlertCircle, Eye, EyeOff, Tag, Check, X } from 'lucide-react';
import { authService } from '../../services/authService';
import { referralApi, referralUtils } from '../../services/referralApi';

interface ValidationErrors {
  email?: string;
  password?: string;
  confirmPassword?: string;
  promoCode?: string;
}

function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedPlan = searchParams.get('plan');
  
  const getPlanDisplayName = (plan: string | null) => {
    switch (plan) {
      case 'expert': return 'Эксперт';
      case 'pro': return 'Pro';
      case 'team': return 'Команда';
      default: return null;
    }
  };
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    promoCode: ''
  });
  const [isAgreed, setIsAgreed] = useState(false);
  const [agreementError, setAgreementError] = useState<string | null>(null);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [promoCodeValidation, setPromoCodeValidation] = useState<{
    isChecking: boolean;
    isValid: boolean | null;
    message: string;
    discountPercent: number;
  }>({
    isChecking: false,
    isValid: null,
    message: '',
    discountPercent: 0
  });

  // Эффект для загрузки кода приглашения из cookie при загрузке страницы
  useEffect(() => {
    const promoCodeFromCookie = referralUtils.getReferralPromoCodeFromCookie();
    if (promoCodeFromCookie) {
      setFormData(prev => ({ ...prev, promoCode: promoCodeFromCookie }));
      validatePromoCode(promoCodeFromCookie);
    }
  }, []);

  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];

    if (password.length < 6) {
      errors.push('Пароль должен содержать минимум 6 символов');
    }
    if (!/[0-9]/.test(password)) {
      errors.push('Пароль должен содержать хотя бы одну цифру');
    }

    return errors;
  };

  const validatePromoCode = async (code: string) => {
    if (!code.trim()) {
      setPromoCodeValidation({
        isChecking: false,
        isValid: null,
        message: '',
        discountPercent: 0
      });
      return;
    }

    setPromoCodeValidation(prev => ({ ...prev, isChecking: true }));
    
    try {
      const validation = await referralApi.validatePromoCode(code.trim());
      setPromoCodeValidation({
        isChecking: false,
        isValid: validation.isValid,
        message: validation.message,
        discountPercent: validation.discountPercent
      });
    } catch (error) {
      setPromoCodeValidation({
        isChecking: false,
        isValid: false,
        message: 'Ошибка при проверке кода приглашения',
        discountPercent: 0
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    // Валидация email
    if (!formData.email) {
      newErrors.email = 'Email обязателен';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Введите корректный email';
    }

    // Валидация пароля
    const passwordErrors = validatePassword(formData.password);
    if (passwordErrors.length > 0) {
      newErrors.password = passwordErrors.join('\n');
    }

    // Проверка совпадения паролей
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Пароли не совпадают';
    }

    // Код приглашения является необязательным, валидация только при наличии значения

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Проверка согласия с условиями
    if (!isAgreed) {
      setAgreementError('Необходимо принять условия использования сервиса');
      return;
    } else {
      setAgreementError(null);
    }

    setIsLoading(true);
    setFormError(null);

    try {
      const response = await authService.register({
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        promoCode: formData.promoCode.trim() || undefined,
        plan: selectedPlan || undefined
      });

      // Проверяем наличие ошибки
      if (!response.success) {
        // Проверяем наиболее распространенные ошибки и отображаем их в соответствующих полях
        if (response.error?.includes('Пользователь с таким Email уже существует')) {
          setErrors(prev => ({
            ...prev,
            email: 'Пользователь с таким Email уже существует'
          }));
        } else if (response.error?.toLowerCase().includes('password')) {
          // Ошибки, связанные с паролем
          setErrors(prev => ({
            ...prev,
            password: response.error
          }));
        } else if (response.error?.includes('Промокод') || response.error?.includes('промокод')) {
          // Ошибки, связанные с промокодом
          setErrors(prev => ({
            ...prev,
            promoCode: response.error
          }));
        } else {
          // Общая ошибка
          setFormError(response.error || 'Ошибка при регистрации');
        }
        setIsLoading(false);
        return;
      }

      // Если нет ошибки, считаем регистрацию успешной
      navigate('/auth/email-confirmation');
      
    } catch (error: any) {
      // Эта часть вряд ли будет выполняться, так как мы изменили обработку в authService
      setFormError('Ошибка соединения с сервером');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 transition-colors duration-200">
      <div className="fixed top-4 right-4">
        <button
          onClick={() => document.documentElement.classList.toggle('dark')}
          className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          aria-label="Toggle theme"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-800 dark:text-gray-200">
            <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"></path>
          </svg>
        </button>
      </div>
      <div className="max-w-md mx-auto px-4 py-12">
        <div className="mb-4">
          <CustomLink
            to="/"
            className="inline-flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            ← Вернуться на главную
          </CustomLink>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            Регистрация
          </h2>

          {selectedPlan && (
            <div className="mb-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                Выбранный тариф: <strong className="text-gray-900 dark:text-white">{getPlanDisplayName(selectedPlan)}</strong>
              </p>
            </div>
          )}

          {formError && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData({ ...formData, email: e.target.value });
                    if (errors.email) setErrors({ ...errors, email: undefined });
                  }}
                  className={`w-full pl-10 p-3 rounded-lg border ${
                    errors.email ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'
                  } bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white`}
                  placeholder="your@email.com"
                  required
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.email}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Пароль
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => {
                    setFormData({ ...formData, password: e.target.value });
                    if (errors.password) setErrors({ ...errors, password: undefined });
                  }}
                  className={`w-full pl-10 pr-10 p-3 rounded-lg border ${
                    errors.password ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'
                  } bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white`}
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  aria-label={showPassword ? "Скрыть пароль" : "Показать пароль"}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Пароль должен содержать минимум 6 символов и хотя бы одну цифру
              </p>
              {errors.password && errors.password.split('\n').map((error, index) => (
                <p key={index} className="mt-1 text-sm text-red-500 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {error}
                </p>
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Подтверждение пароля
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={(e) => {
                    setFormData({ ...formData, confirmPassword: e.target.value });
                    if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: undefined });
                  }}
                  className={`w-full pl-10 pr-10 p-3 rounded-lg border ${
                    errors.confirmPassword ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'
                  } bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white`}
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  aria-label={showConfirmPassword ? "Скрыть пароль" : "Показать пароль"}
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.confirmPassword}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Код приглашения <span className="text-gray-400 text-xs font-normal">(необязательно)</span>
              </label>
              <div className="relative">
                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={formData.promoCode}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    setFormData({ ...formData, promoCode: value });
                    if (errors.promoCode) setErrors({ ...errors, promoCode: undefined });
                    validatePromoCode(value);
                  }}
                  className={`w-full pl-10 pr-10 p-3 rounded-lg border ${
                    errors.promoCode ? 'border-red-500' : 
                    promoCodeValidation.isValid === true ? 'border-green-500' :
                    promoCodeValidation.isValid === false ? 'border-red-500' :
                    'border-gray-200 dark:border-gray-700'
                  } bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white`}
                  placeholder="Введите код приглашения"
                  maxLength={20}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {promoCodeValidation.isChecking ? (
                    <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  ) : promoCodeValidation.isValid === true ? (
                    <Check className="w-5 h-5 text-green-500" />
                  ) : promoCodeValidation.isValid === false ? (
                    <X className="w-5 h-5 text-red-500" />
                  ) : null}
                </div>
              </div>
              {errors.promoCode && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.promoCode}
                </p>
              )}
              {!errors.promoCode && promoCodeValidation.message && (
                <p className={`mt-1 text-sm flex items-center ${
                  promoCodeValidation.isValid ? 'text-green-600 dark:text-green-400' : 'text-red-500'
                }`}>
                  {promoCodeValidation.isValid ? (
                    <Check className="w-4 h-4 mr-1" />
                  ) : (
                    <AlertCircle className="w-4 h-4 mr-1" />
                  )}
                  {promoCodeValidation.message}
                </p>
              )}
            </div>

            {agreementError && (
              <p className="text-sm text-red-600 dark:text-red-400 mb-2">
                {agreementError}
              </p>
            )}
            
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                id="agreement"
                checked={isAgreed}
                onChange={(e) => {
                  setIsAgreed(e.target.checked);
                  if (e.target.checked) {
                    setAgreementError(null);
                  }
                }}
                className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded shadow-sm"
              />
              <label htmlFor="agreement" className="text-sm text-gray-700 dark:text-gray-300">
                Я принимаю{' '}
                <Link 
                  to="/public-offer" 
                  target="_blank"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  Публичную оферту
                </Link>
                {' '}и{' '}
                <Link 
                  to="/privacy-policy" 
                  target="_blank"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  Политику конфиденциальности и условия обработки персональных данных
                </Link>
              </label>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              variant="primary-filled"
              fullWidth
              className="flex items-center justify-center"
            >
              {isLoading ? 'Регистрация...' : 'Зарегистрироваться'}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
            Уже есть аккаунт?{' '}
            <CustomLink
              to="/auth/login"
              className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
            >
              Войти
            </CustomLink>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Register;
